import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useLanguage } from "../../contexts/LanguageContext";
import { fetchAPI } from "../../lib/utils";
import { Button } from "../ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
} from "../ui/dialog";

/**
 * Pubblicare un modello personale, sapendo prima cosa succede (D10, D12).
 *
 * Pubblicato, il modello vale per gli atleti del coach in quello sport al posto
 * di quello di sistema. Ma se un atleta e' seguito anche da un altro coach con
 * un suo modello pubblicato, resta su quello di sistema finche' qualcuno non
 * sceglie a mano (D2): il coach lo deve sapere **prima**, con i numeri, invece
 * di scoprirlo dalle lamentele.
 */
export function PublishModelDialog({ model, open, onOpenChange, onPublished }) {
	const { t } = useLanguage();
	const [impact, setImpact] = useState(null);
	const [loading, setLoading] = useState(false);
	const [saving, setSaving] = useState(false);

	useEffect(() => {
		if (!open || !model?.model_id) return undefined;
		let cancelled = false;
		setImpact(null);
		setLoading(true);
		fetchAPI(`/performance-models/${encodeURIComponent(model.model_id)}/impact`)
			.then((data) => {
				if (!cancelled) setImpact(data);
			})
			.catch(() => {
				if (!cancelled) setImpact(null);
			})
			.finally(() => {
				if (!cancelled) setLoading(false);
			});
		return () => {
			cancelled = true;
		};
	}, [open, model?.model_id]);

	const publish = async () => {
		setSaving(true);
		try {
			const updated = await fetchAPI(
				`/performance-models/${encodeURIComponent(model.model_id)}`,
				{ method: "PUT", body: JSON.stringify({ is_published: true }) },
			);
			toast.success(t("scModelPublished"));
			onOpenChange(false);
			onPublished?.(updated);
		} catch (error) {
			toast.error(`${t("error")}: ${error?.message || ""}`);
		} finally {
			setSaving(false);
		}
	};

	const owners = (impact?.conflict_owners || []).map((o) => o.name).join(", ");

	return (
		<Dialog open={open} onOpenChange={(next) => !saving && onOpenChange(next)}>
			<DialogContent className="bg-card border-border max-w-md">
				<DialogDescription className="hidden" />
				<DialogHeader>
					<DialogTitle>{t("scPublishTitle")}</DialogTitle>
				</DialogHeader>
				<div className="space-y-3 text-sm">
					{loading ? (
						<div className="flex items-center gap-2 text-muted-foreground">
							<Loader2 className="w-4 h-4 animate-spin" />
							{t("loading")}
						</div>
					) : impact ? (
						<>
							<p>
								{t("scPublishUsing")
									.replace("{using}", impact.using_model ?? 0)
									.replace("{athletes}", impact.athletes ?? 0)}
							</p>
							{impact.manual_other > 0 && (
								<p className="text-muted-foreground">
									{t("scPublishManual").replace("{count}", impact.manual_other)}
								</p>
							)}
							{impact.conflicts > 0 && (
								<p className="rounded-sm border border-amber-500/30 bg-amber-500/10 p-2 text-amber-200">
									{t("scPublishConflicts")
										.replace("{count}", impact.conflicts)
										.replace("{owners}", owners || t("scAnotherCoach"))}
								</p>
							)}
						</>
					) : (
						<p className="text-muted-foreground">{t("scPublishGeneric")}</p>
					)}
					<p className="text-xs text-muted-foreground">{t("scPublishReversible")}</p>
					<div className="flex justify-end gap-2 pt-1">
						<Button
							variant="ghost"
							className="rounded-sm"
							disabled={saving}
							onClick={() => onOpenChange(false)}
						>
							{t("cancel")}
						</Button>
						<Button
							className="bg-volt hover:bg-volt/90 rounded-sm"
							disabled={saving || loading}
							onClick={publish}
						>
							{saving && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />}
							{t("scPublish")}
						</Button>
					</div>
				</div>
			</DialogContent>
		</Dialog>
	);
}
